import { apiRequest, readJsonOrThrow } from "@/api/client";

export const CLIPR_DEMO_COLLECTION_JOB_ID = "demo-vod-example";

function asTrimmedString(value) {
  return String(value || "").trim();
}

function isMp4Clip(asset) {
  const relativePath = asTrimmedString(asset?.relativePath).toLowerCase();
  const videoUrl = asTrimmedString(asset?.videoUrl).toLowerCase();
  return relativePath.endsWith(".mp4") || videoUrl.endsWith(".mp4");
}

const DEMO_CLIP_METADATA = {
  es: [
    {
      title: "El momento que nadie vio venir",
      description: "Un giro inesperado que convirtió este directo en un clip imperdible.",
      hashtags: ["streamer", "gaming", "clips"],
      confidence: 0.96,
    },
    {
      title: "Cuando todo cambia en segundos",
      description: "La reacción perfecta para compartir con tu comunidad.",
      hashtags: ["stream", "viral", "gaming"],
      confidence: 0.93,
    },
    {
      title: "La jugada que salvó la partida",
      description: "Un momento clave del stream listo para destacar en redes.",
      hashtags: ["gaming", "highlight", "streamer"],
      confidence: 0.74,
    },
    {
      title: "No esperaba que pasara esto",
      description: "Un clip corto, directo y con potencial para captar atención.",
      hashtags: ["clips", "reels", "gaming"],
      confidence: 0.68,
    },
    {
      title: "El mejor momento del directo",
      description: "Una escena que merece llegar a más personas.",
      hashtags: ["tiktok", "stream", "viral"],
      confidence: 0.87,
    },
  ],
  en: [
    {
      title: "The moment nobody saw coming",
      description: "An unexpected turn made this stream an unforgettable clip.",
      hashtags: ["streamer", "gaming", "clips"],
      confidence: 0.96,
    },
    {
      title: "When everything changes in seconds",
      description: "The perfect reaction to share with your community.",
      hashtags: ["stream", "viral", "gaming"],
      confidence: 0.93,
    },
    {
      title: "The play that saved the game",
      description: "A key stream moment ready to stand out on social media.",
      hashtags: ["gaming", "highlight", "streamer"],
      confidence: 0.74,
    },
    {
      title: "I did not expect this to happen",
      description: "A short, direct clip built to capture attention.",
      hashtags: ["clips", "reels", "gaming"],
      confidence: 0.68,
    },
    {
      title: "The best moment from the stream",
      description: "A scene that deserves to reach a bigger audience.",
      hashtags: ["tiktok", "stream", "viral"],
      confidence: 0.87,
    },
  ],
};

function buildSyntheticClip(asset, index, jobId, vodThumbnailUrl, language) {
  const relativePath = asTrimmedString(asset?.relativePath);
  const videoUrl = asTrimmedString(asset?.videoUrl);
  if (!videoUrl) return null;

  const rawName = asTrimmedString(asset?.name) || relativePath.split("/").pop() || `clip-${index + 1}.mp4`;
  const localizedMetadata = DEMO_CLIP_METADATA[language];
  const metadata = localizedMetadata[index % localizedMetadata.length];
  const clipTitle = metadata.title;
  const clipId = `${jobId}-clip-${index + 1}`;
  const thumbnailUrl = asTrimmedString(asset?.thumbnailUrl) || asTrimmedString(vodThumbnailUrl) || null;

  return {
    id: clipId,
    fallbackKey: clipId,
    job_id: jobId,
    job_id_short: String(jobId).slice(0, 8),
    status: "4_rendered",
    originalStatus: "4_rendered",
    clip_active: true,
    rendered_video_url: videoUrl,
    playbackUrl: videoUrl,
    thumbnailUrl,
    jobThumbnailUrl: asTrimmedString(vodThumbnailUrl) || null,
    clipTitle,
    title: clipTitle,
    clip_description: metadata.description,
    clip_tags: metadata.hashtags,
    confidence: metadata.confidence,
    name: rawName,
    relativePath,
    source_username: null,
    startSeconds: 0,
    endSeconds: 29,
    durationLabel: "0:29",
    timeRangeLabel: "0:00 - 0:29",
    edit_source_url: videoUrl,
    edit_window_start_seconds: 0,
    edit_window_end_seconds: 29,
    effective_video_type: "gameplay",
    clip_lenguage: language,
    created_at: "1970-01-01T00:00:00Z",
    updated_at: "1970-01-01T00:00:00Z",
    isUpdatingStatus: false,
    isDemo: true,
    isReadOnly: true,
  };
}

export async function fetchCliprDemoAssets(session, language) {
  const query = new URLSearchParams({ language });
  const response = await apiRequest(`/momo/clipr/assets?${query.toString()}`, {
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to fetch clipr demo assets");
}

export function mapDemoAssetsToScheduleCollection(
  payload,
  { translate, jobId = CLIPR_DEMO_COLLECTION_JOB_ID, language } = {}
) {
  const t = typeof translate === "function"
    ? translate
    : (_key, fallback) => fallback;

  const vodThumbnailUrl = asTrimmedString(payload?.vodThumbnailUrl) || null;
  const rawClips = Array.isArray(payload?.clips) ? payload.clips : [];
  const clips = rawClips
    .filter((asset) => isMp4Clip(asset))
    .map((asset, index) => buildSyntheticClip(asset, index, jobId, vodThumbnailUrl, language))
    .filter(Boolean);

  const displayTitle = t("schedulePost.demoVodTitle", "VOD de prueba");
  const collectionThumbnail = vodThumbnailUrl || clips[0]?.thumbnailUrl || null;
  const collection = {
    jobId,
    jobIdShort: String(jobId).slice(0, 8),
    sourceUsername: null,
    clips,
    thumbnailUrl: collectionThumbnail,
    jobThumbnailUrl: collectionThumbnail,
    readyCount: clips.length,
    totalClips: clips.length,
    displayTitle,
    jobStatus: "6_rendered",
    jobErrorCode: null,
    jobErrorMessage: null,
    jobCreatedAt: null,
    jobUpdatedAt: null,
    isDemo: true,
    isReadOnly: true,
  };

  return { collection, clips };
}
